import React, { useEffect, useState } from 'react';
import DataTable, { TableColumn } from 'react-data-table-component';
import { Card, CardBody, Col, Row, Input, Label } from 'reactstrap';
import DeleteConfirm from '../../../Components/Modals/Delete';
import { get, postJson } from '../../../hook/request';
import { H2, Btn } from '../../../AbstractElements';
import Favourite from '../../../Components/Widgets/Favourite';
import { toast } from 'react-toastify';

type Attivita = {
  ticketID?: string | null;
  ID: number;
  progettoID: number;
  descrizione: string | null;
  tipo_nome: string;
  tipo: number;
  tempo_esecuzione: string | null;
  data_esecuzione: string | null;
  utenti: string;
};

type ReportProgetto = {
  ragionesociale: string;
  nomeProgetto: string;
  tempo_totale: number;
  tempo_clienti: number;
  tempo_factory: number;
  attivita: Attivita[];
};

type RapportiniResponse = Record<string, ReportProgetto>;

type ReportRow = ReportProgetto & {
  progettoID: number;
};

function fmtDateTime(db?: string | null): string {
  if (!db) return '-';
  const trimmed = db.trim();
  const [datePart] = trimmed.split(' ');
  const [y, m, d] = datePart.split('-');
  return `${d}/${m}/${y}`; 
}

function fmtTime(t?: string | null): string {
  if (!t) return '-';
  return t.slice(0, 5);
}

function fmtTotaleOre(n: number): string {
  return `${n} h`;
}

function toNumberOrUndefined(val: string): number | undefined {
  if (val === '' || val == null) return undefined;
  const n = Number(val);
  return Number.isFinite(n) ? n : undefined;
}

function getMonthStart(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

function getMonthEnd(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
}

function getTodayDate(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

type OverrideState = {
  oreCliente?: string;
  oreIFactory?: string;
  editedCliente?: boolean;
  editedIFactory?: boolean;
  isEditingCliente?: boolean;
  isEditingIFactory?: boolean;
};

export default function RapportiniClienti() {
  const [reportRows, setReportRows] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [defaultStart] = useState<string>(() => getMonthStart());
  const [defaultEnd] = useState<string>(() => getMonthEnd());
  const [filterStartDate, setFilterStartDate] = useState<string>(defaultStart);
  const [filterEndDate, setFilterEndDate] = useState<string>(defaultEnd);
  const [filterCliente, setFilterCliente] = useState<string>('');
  const [localStartDate, setLocalStartDate] = useState<string>(defaultStart);
  const [localEndDate, setLocalEndDate] = useState<string>(defaultEnd);
  const [localCliente, setLocalCliente] = useState<string>('');

  const hasActiveFilter = !!(filterStartDate || filterEndDate || filterCliente.trim());

  const [overridesByProgetto, setOverridesByProgetto] = useState<Record<number, OverrideState>>({});
  const [reportDateByProgetto, setReportDateByProgetto] = useState<Record<number, string>>({});

  const setOverrideField = (
    progettoID: number,
    field: 'oreCliente' | 'oreIFactory',
    value: string
  ) => {
    setOverridesByProgetto(prev => {
      const prevRow = prev[progettoID] ?? {};
      const editedFlag = field === 'oreCliente' ? 'editedCliente' : 'editedIFactory';

      return {
        ...prev,
        [progettoID]: {
          ...prevRow,
          [field]: value,
          [editedFlag]: true, 
        },
      };
    });
  };

  const toggleEditing = (
    progettoID: number,
    which: 'cliente' | 'ifactory',
    defaultValue: number
  ) => {
    setOverridesByProgetto(prev => {
      const row = prev[progettoID] ?? {};

      if (which === 'cliente') {
        const nextIsEditing = !row.isEditingCliente;
        if (!nextIsEditing) {
          const curr = toNumberOrUndefined(row.oreCliente ?? '');
          const currNum = curr ?? defaultValue;

          const sameAsDefault = currNum === defaultValue;

          return {
            ...prev,
            [progettoID]: {
              ...row,
              isEditingCliente: false,
              ...(sameAsDefault
                ? { oreCliente: undefined, editedCliente: false }
                : { editedCliente: true }),
            },
          };
        }
        return {
          ...prev,
          [progettoID]: {
            ...row,
            isEditingCliente: true,
            oreCliente: row.oreCliente ?? String(defaultValue),
          },
        };
      }

      const nextIsEditing = !row.isEditingIFactory;

      if (!nextIsEditing) {
        const curr = toNumberOrUndefined(row.oreIFactory ?? '');
        const currNum = curr ?? defaultValue;

        const sameAsDefault = currNum === defaultValue;

        return {
          ...prev,
          [progettoID]: {
            ...row,
            isEditingIFactory: false,
            ...(sameAsDefault
              ? { oreIFactory: undefined, editedIFactory: false }
              : { editedIFactory: true }),
          },
        };
      }

      return {
        ...prev,
        [progettoID]: {
          ...row,
          isEditingIFactory: true,
          oreIFactory: row.oreIFactory ?? String(defaultValue),
        },
      };
    });
  };




  async function loadReport(startDate?: string, endDate?: string, qCliente?: string) {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('data_inizio', startDate);
      if (endDate) params.set('data_fine', endDate);
      if (qCliente?.trim()) params.set('qCliente', qCliente.trim());

      const url = `/report/RapportiniClienti${params.toString() ? `?${params.toString()}` : ''}`;

      const res = await get<any>(url);
      const payload: RapportiniResponse = (res as any)?.data ?? res;

      //ordino per ragione sociale
      const list = Object.entries(payload || {})
      .map(([progettoID, value]) => ({ progettoID: Number(progettoID), ...value }))
      .sort((a,b) => (a.ragionesociale ?? '').localeCompare(b.ragionesociale ?? ''));

      setReportRows(list);
    } catch (err) {
      console.error(err);
      toast.error('Errore nel caricamento rapportini');
      setReportRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadReport(defaultStart, defaultEnd, '');
  }, [defaultStart, defaultEnd]);

  async function SendReport(progettoID: number) {
    try {
      const report = reportRows.find(r => r.progettoID === progettoID);
      if (!report) {
        toast.error('Progetto non trovato');
        return;
      }

      const ov = overridesByProgetto[progettoID] ?? {};

      const defaultCliente = Number(report.tempo_clienti ?? 0);
      const defaultFactory = Number(report.tempo_factory ?? 0);
      const currentClienteNum = toNumberOrUndefined(ov.oreCliente ?? '') ?? defaultCliente;
      const currentFactoryNum = toNumberOrUndefined(ov.oreIFactory ?? '') ?? defaultFactory;

      if (currentClienteNum < 0) {
        toast.error('Ore cliente non può essere negativo');
        return;
      }
      if (currentFactoryNum < 0) {
        toast.error('Ore i-factory non può essere negativo');
        return;
      }

      const params = new URLSearchParams();
      params.set('ID', String(progettoID));
      params.set('data', reportDateByProgetto[progettoID] || getTodayDate());


      if (filterStartDate) params.set('data_inizio', filterStartDate);
      if (filterEndDate) params.set('data_fine', filterEndDate);

      const shouldSendCliente = !!ov.isEditingCliente || (!!ov.editedCliente && currentClienteNum !== defaultCliente);
      const shouldSendIFactory =!!ov.isEditingIFactory || (!!ov.editedIFactory && currentFactoryNum !== defaultFactory);
      if (shouldSendCliente) {
        params.set('ore_cliente', String(currentClienteNum));
      }
      if (shouldSendIFactory) {
        params.set('ore_ifactory', String(currentFactoryNum));
      }



      const res = await get(`/report/creaRapportino?${params.toString()}`);
      const body: any = (res as any)?.data ?? res;

      toast.success('Rapportino creato con successo');
      window.open(body, '_blank');

      // (opzionale) reset flags dopo invio
      setOverridesByProgetto(prev => ({
        ...prev,
        [progettoID]: {
          ...prev[progettoID],
          editedCliente: false,
          editedIFactory: false,
          isEditingCliente: false,
          isEditingIFactory: false,
        },
      }));
    } catch (err) {
      console.error(err);
      toast.error('Errore nella creazione del rapportino');
    }
  }

  async function disattivaRapportino(id: number) {
    const res = await postJson<{ status?: string; esito?: string; message?: string }>('/progetti/dissRapport', { ID: id });
    if (['error', 'ko'].includes((res.data?.status ?? '').toLowerCase()) || res.data?.esito === 'KO') {
      throw new Error(res.data.message || 'Errore nella disattivazione del rapportino');
    }
  }

  const columns: TableColumn<Attivita>[] = [
      {
        name: 'Data esecuzione',
        selector: r => r.data_esecuzione ?? '',
        sortable: true,
        cell: r => fmtDateTime(r.data_esecuzione),
        width: '160px',
      },
      {
        name: 'Ticket ID',
        selector: r => r.ticketID ?? '',
        sortable: true,
        minWidth: '220px',
        cell: r => r.ticketID || '-',
      },
      {
        name: 'Descrizione',
        selector: r => r.descrizione ?? '',
        sortable: true,
        grow: 2,
        cell: r => r.descrizione || '-',
      },
      {
        name: 'Tipo',
        selector: r => r.tipo_nome,
        sortable: true,
        width: '150px',
      },
      {
        name: 'Tempo esecuzione',
        selector: r => r.tempo_esecuzione ?? '',
        sortable: true,
        cell: r => fmtTime(r.tempo_esecuzione),
        width: '200px',
        right: true,
      },
      {
        name: 'Utenti',
        selector: r => r.utenti,
        sortable: true,
        grow: 1.5,
      },
      {
        name: 'Azioni',
        width: '160px',
        button: true,
        cell: r => (
          <DeleteConfirm
            title="Conferma disattivazione"
            message="Sei sicuro di voler disattivare questo rapportino?"
            confirmText="Disattiva"
            successMessage="Rapportino disattivato"
            errorMessage="Errore nella disattivazione del rapportino"
            onConfirm={() => disattivaRapportino(r.ID)}
            onSuccess={() => { loadReport(filterStartDate, filterEndDate, filterCliente); }}
            trigger={<Btn type="button" color="danger" size="sm" disabled={loading}>Disattiva</Btn>}
          />
        ),
      },
    ];

  return (
    <div className="page-body pt-4">
      <Card>
        <CardBody>
          <Col xl={12} className="mb-lg-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <H2 className="mb-0">Rapportini Clienti</H2>
              <Favourite percorso={'/report/RapportiniClienti'} nome={'Rapportini Clienti'} />
            </div>

            <div className="d-flex justify-content-between align-items-center">
              <Btn
                className="px-3"
                color={hasActiveFilter ? 'success' : 'primary'}
                type="button"
                onClick={() => setShowFilter((prev: boolean) => !prev)}
                disabled={loading}
              >
                Filtri {hasActiveFilter && '(attivi)'}
              </Btn>
            </div>

            {showFilter && (
              <Card className="mt-3 shadow-sm" style={{ backgroundColor: '#fcfcfc' }}>
                <CardBody>
                  <Row className="g-3 align-items-end">
                    <Col md={3}>
                      <Label htmlFor="f_data_inizio">Data inizio</Label>
                      <Input
                        id="f_data_inizio"
                        type="date"
                        value={localStartDate}
                        onChange={e => setLocalStartDate(e.target.value)}
                        disabled={loading}
                      />
                    </Col>

                    <Col md={3}>
                      <Label htmlFor="f_data_fine">Data fine</Label>
                      <Input
                        id="f_data_fine"
                        type="date"
                        value={localEndDate}
                        onChange={e => setLocalEndDate(e.target.value)}
                        disabled={loading}
                      />
                    </Col>

                    <Col md={3}>
                      <Label htmlFor="f_cliente">Ragione Sociale</Label>
                      <Input
                        id="f_cliente"
                        type="text"
                        value={localCliente}
                        onChange={e => setLocalCliente(e.target.value)}
                        placeholder="Cerca cliente..."
                        disabled={loading}
                      />
                    </Col>

                    <Col md={3} className="d-flex gap-2">
                      <Btn
                        color="primary"
                        type="button"
                        onClick={() => {
                          setFilterStartDate(localStartDate);
                          setFilterEndDate(localEndDate);
                          setFilterCliente(localCliente.trim());
                          loadReport(localStartDate || undefined, localEndDate || undefined, localCliente);
                        }}
                        disabled={loading}
                      >
                        Applica filtri
                      </Btn>

                      <Btn
                        color="secondary"
                        type="button"
                        onClick={() => {
                          setLocalStartDate(defaultStart);
                          setLocalEndDate(defaultEnd);
                          setLocalCliente('');
                          setFilterStartDate(defaultStart);
                          setFilterEndDate(defaultEnd);
                          setFilterCliente('');
                          loadReport(defaultStart, defaultEnd, '');
                        }}
                        disabled={loading}
                      >
                        Reset filtri
                      </Btn>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            )}
          </Col>
        </CardBody>
      </Card>

      {reportRows.map(report => {
        const ov = overridesByProgetto[report.progettoID] ?? {};
        const oreClienteValue = ov.oreCliente ?? String(report.tempo_clienti ?? '');
        const oreIFactoryValue = ov.oreIFactory ?? String(report.tempo_factory ?? '');
        const reportDateValue = reportDateByProgetto[report.progettoID] || getTodayDate();

        const isEditingCliente = !!ov.isEditingCliente;
        const isEditingIFactory = !!ov.isEditingIFactory;

        return (
          <Col xl={12} className="mb-lg-3" key={report.progettoID}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <h5 className="mb-0"><b>{report.ragionesociale} </b>- {report.nomeProgetto}</h5>
                  </div>
                  <div className="text-end">
                    <Btn onClick={() => SendReport(report.progettoID)} color="success">
                      <i className="fa-solid fa-print"></i>
                    </Btn>
                  </div>
                </div>

                <div className="table-responsive">
                  <DataTable
                    columns={columns}
                    data={report.attivita}
                    progressPending={loading}
                    striped
                    highlightOnHover
                    dense
                    noDataComponent="Nessuna attività per questo progetto"
                    pagination={false}
                  />
                </div>

                <div className="mt-3 d-flex flex-column flex-md-row gap-3 justify-content-end align-items-md-end">
                  <div style={{ minWidth: 170 }}>
                    <Label className="mb-1" for={`dataRapportino_${report.progettoID}`}>
                      Data
                    </Label>

                    <Input
                      id={`dataRapportino_${report.progettoID}`}
                      type="date"
                      value={reportDateValue}
                      onChange={e =>
                        setReportDateByProgetto(prev => ({
                          ...prev,
                          [report.progettoID]: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div style={{ minWidth: 160 }}>
                    <Label className="mb-1" for={`oreCliente_${report.progettoID}`}>
                      Totale Ore
                    </Label>

                    <Input disabled={true} value={fmtTotaleOre(report.tempo_totale)} />
                  </div>

 <div style={{ minWidth: 160 }}>
                    <Label className="mb-1" for={`oreIFactory_${report.progettoID}`}>
                      Ore Manutenzione
                    </Label>

                    <div className="d-flex align-items-center gap-2">
                      <Btn
                        color={isEditingIFactory ? 'primary' : 'light'}
                        type="button"
                        className="px-2"
                        onClick={() => toggleEditing(report.progettoID, 'ifactory', Number(report.tempo_factory ?? 0))}
                        title={isEditingIFactory ? 'Modifica attiva' : 'Modifica'}
                      >
                        <i className="fa-solid fa-pencil"></i>
                      </Btn>

                      <Input
                        id={`oreIFactory_${report.progettoID}`}
                        type="number"
                        step="0.25"
                        min="0"
                        disabled={!isEditingIFactory}
                        value={oreIFactoryValue}
                        onChange={e =>
                          setOverrideField(report.progettoID, 'oreIFactory', e.target.value)
                        }
                      />
                    </div>
                  </div>


                  <div style={{ minWidth: 160 }}>
                    <Label className="mb-1" for={`oreCliente_${report.progettoID}`}>
                      Ore Svluppo
                    </Label>

                    <div className="d-flex align-items-center gap-2">
                      <Btn
                        color={isEditingCliente ? 'primary' : 'light'}
                        type="button"
                        className="px-2"
                        onClick={() => toggleEditing(report.progettoID, 'cliente', Number(report.tempo_clienti ?? 0))}
                        title={isEditingCliente ? 'Modifica attiva' : 'Modifica'}
                      >
                        <i className="fa-solid fa-pencil"></i>
                      </Btn>

                      <Input
                        id={`oreCliente_${report.progettoID}`}
                        type="number"
                        step="0.25"
                        min="0"
                        disabled={!isEditingCliente}
                        value={oreClienteValue}
                        onChange={e =>
                          setOverrideField(report.progettoID, 'oreCliente', e.target.value)
                        }
                      />
                    </div>
                  </div>

                 
                </div>
              </CardBody>
            </Card>
          </Col>
        );
      })}

      {!loading && reportRows.length === 0 && (
        <Col xl={12}>
          <Card>
            <CardBody>Nessun dato disponibile per i filtri selezionati</CardBody>
          </Card>
        </Col>
      )}
    </div>
  );
}
